import {arNum, ASPECT_CSS, fileSize, formatTime} from '../format';
import {assetUrl, useStudio} from '../store';
import type {Message, Scene} from '../types';

import {Elapsed} from './Elapsed';
import {Download, Regenerate} from './Icons';

/**
 * Export.
 *
 * The render endpoint is synchronous and reports no progress, so there is no honest
 * percentage to show while it runs — an indeterminate bar plus elapsed time says what is
 * actually known. Once it finishes, the outcome is read back from the document, so the
 * panel is right even after a reload.
 */
export function RenderPanel({scenes}: {scenes: Scene[]}) {
  const s = useStudio();
  const {render} = s;
  const aspect = s.project!.aspect_ratio;
  const renderable = scenes.filter((sc) => assetUrl(sc.visual)).length;

  return (
    <div className="panel">
      <div className="panel-head tight">
        <span className="h-title">الإنتاج</span>
        <span className="chip solid">{aspect}</span>
      </div>
      <div className="rail-body">
        {render.phase === 'running' && (
          <>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8}}>
              <span style={{font: '700 13.5px/1 var(--sans)', color: 'var(--state-work)'}}>
                جاري الإنتاج النهائي
              </span>
              <span style={{font: '600 13px/1 var(--sans)', color: 'var(--state-work)'}}>
                <Elapsed />
              </span>
            </div>
            <div className="bar">
              <div className="shimmer" style={{width: '100%'}} />
            </div>
            <p className="empty-note" style={{marginTop: 10}}>
              دمج الصوت مع المشاهد… لا تغلق الصفحة.
            </p>
          </>
        )}

        {render.phase === 'done' && render.url && (
          <>
            <div
              style={{
                border: '1px solid var(--rule)',
                borderRadius: 4,
                background: '#241E1C',
                aspectRatio: ASPECT_CSS[aspect],
                maxHeight: 250,
                margin: '0 auto 14px',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <video
                src={render.url}
                controls
                playsInline
                preload="metadata"
                style={{position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain'}}
              />
            </div>
            <button type="button" className="btn md" onClick={() => s.go('preview')}>
              افتح المعاينة الكاملة
            </button>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                marginTop: 12,
                borderTop: '1px solid var(--rule)',
                paddingTop: 12,
              }}
            >
              <div>
                <div style={{font: '600 12.5px/1.6 var(--sans)', color: 'var(--ink)'}}>
                  النسخة النهائية · {aspect}
                </div>
                <div className="empty-note" style={{fontSize: 11.5}}>
                  MP4 · {render.sizeBytes === null ? '—' : fileSize(render.sizeBytes)}
                </div>
              </div>
              <a className="btn-out" href={render.url} download>
                <Download />
                تنزيل
              </a>
            </div>
            <button
              type="button"
              className="btn-txt"
              style={{marginTop: 8}}
              onClick={() => void s.startRender()}
            >
              <Regenerate />
              أعد الإنتاج
            </button>
          </>
        )}

        {(render.phase === 'idle' || render.phase === 'failed') && (
          <>
            <p className="empty-note" style={{margin: '0 0 14px'}}>
              {renderable
                ? `${arNum(renderable)} من ${arNum(scenes.length)} مشاهد جاهزة للإنتاج. المدة المتوقعة 15–20 ثانية.`
                : 'لا يمكن الإنتاج قبل توفّر مشهد مرئي واحد على الأقل.'}
            </p>
            <button
              type="button"
              className="btn md"
              disabled={renderable === 0}
              onClick={() => void s.startRender()}
            >
              أنتج الفيديو
            </button>
            {render.phase === 'failed' ? (
              <div className="err-note" style={{marginTop: 12}}>
                {render.message}
              </div>
            ) : (
              <div
                className="empty-note"
                style={{marginTop: 14, borderTop: '1px solid var(--rule)', paddingTop: 12}}
              >
                لا توجد نسخ جاهزة بعد
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The pipeline actions.
 *
 * Per-scene buttons cover one scene at a time; these are the batch equivalents, and they
 * go through the job queue rather than the request, because generating visuals for a
 * whole storyboard takes minutes.
 */
export function PipelinePanel({scenes}: {scenes: Scene[]}) {
  const s = useStudio();
  const busy = s.isBusy('batch');
  const error = s.errorFor('batch');
  const missingVisuals = scenes.filter((sc) => !assetUrl(sc.visual)).length;
  const missingVoice = scenes.filter((sc) => sc.vo_text && !sc.voiceover?.s3_key).length;
  const disabled = busy || scenes.length === 0;

  return (
    <div className="panel">
      <div className="panel-head tight">
        <span className="h-title">خط الإنتاج</span>
        <span className="h-note">يُنفَّذ عبر قائمة المهام</span>
      </div>
      <div className="rail-body rail-acts">
        <button type="button" className="btn-out" disabled={disabled} onClick={() => void s.batchVisuals()}>
          ولّد المشاهد المرئية
          {missingVisuals > 0 && ` (${arNum(missingVisuals)} ناقص)`}
        </button>
        <button type="button" className="btn-out" disabled={disabled} onClick={() => void s.batchArchive()}>
          طابِق المشاهد الناقصة من الأرشيف
        </button>
        <button type="button" className="btn-out" disabled={disabled} onClick={() => void s.batchVoiceovers()}>
          ولّد التعليق الصوتي
          {missingVoice > 0 && ` (${arNum(missingVoice)} ناقص)`}
        </button>
        {error && <div className="err-note">{error}</div>}
      </div>
    </div>
  );
}

/**
 * The history trail: every brief, edit and regeneration in order.
 *
 * This is what lets someone reopen a project and see how it got here, and it is the
 * human-review evidence the brief asks us to keep — so it is a first-class panel, not a
 * debug log.
 */
export function ActivityLog({messages}: {messages: Message[]}) {
  const rows = [...messages].reverse();

  const dotColor = (m: Message) => {
    if (m.text.includes('فشل') || m.action.includes('failed')) return 'var(--state-fail)';
    return m.role === 'user' ? 'var(--maroon-soft)' : 'var(--state-ok)';
  };

  return (
    <div className="panel">
      <div className="panel-head tight">
        <span className="h-title">سجل النشاط</span>
        <span className="h-note">{arNum(rows.length)} حركة</span>
      </div>
      <div className="log-wrap">
        {rows.length === 0 ? (
          <p className="empty-note" style={{padding: '12px 0'}}>
            لا حركات بعد.
          </p>
        ) : (
          rows.map((m, i) => (
            <div className="log" key={`${m.at}-${i}`}>
              <div className="dot" style={{background: dotColor(m)}} />
              <div style={{minWidth: 0}}>
                <p>{m.text}</p>
                <div className="log-meta">
                  <span>{formatTime(m.at)}</span>
                  <span style={{color: m.role === 'user' ? 'var(--body)' : 'var(--maroon)'}}>
                    {m.role === 'user' ? 'أنت' : 'النظام'}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
